// Tabla anatómica del pack: rangos articulares (ROM) y datos de marcha normativos.
//
// Todos los canales están en GRADOS (excepto desplazamientos en metros y valores 0..1 de la cara) y con signo
// anatómico, no de hueso: flexión +, abducción +, rotación/inclinación a la DERECHA del avatar +.
// La correspondencia con los ejes del esqueleto está calibrada en rig.js.
import { periodic } from './math.js';

/**
 * Rangos de movimiento articular activo de un adulto sano (valores típicos de goniometría clínica, AAOS /
 * Kapandji), redondeados. Ningún clip del pack debe salirse de ellos (lo verifica check.mjs).
 * Rótulo: [mínimo, máximo] en grados.
 */
export const ROM = {
  'pelvis.tilt': [-15, 20],
  'pelvis.obl': [-12, 12],
  'pelvis.rot': [-20, 20],
  'spine.flex': [-25, 55], // total columna respecto de la pelvis (flexión + / extensión -)
  'spine.bend': [-30, 30],
  'spine.rot': [-40, 40],
  'neck.flex': [-40, 40],
  'neck.bend': [-30, 30],
  'neck.rot': [-55, 55],
  'head.flex': [-25, 25],
  'head.bend': [-20, 20],
  'head.rot': [-40, 40], // cuello + cabeza suman ~95° de rotación cervical total
  'shoulder.flex': [-60, 180],
  'shoulder.abd': [-5, 170],
  'shoulder.elev': [-10, 12], // clavícula: elevación + / depresión -
  'elbow.flex': [-3, 145],
  'hip.flex': [-20, 120],
  'hip.abd': [-30, 45], // abducción + / aducción -
  'knee.flex': [-2, 140],
  'ankle.pf': [-20, 50], // flexión plantar + / dorsiflexión -
};

/** Rango para un canal como 'L.hip.flex' (se ignora el lado) o 'pelvis.tilt'. */
export function romFor(channel) {
  const key = channel.replace(/^[LR]\./, '');
  return ROM[key] || null;
}

/**
 * Cinemática de la marcha normal en plano (ciclo desde el contacto del talón, 0..1), en grados.
 * Puntos clave tomados de las curvas normativas habituales de marcha adulta a ~1.2–1.4 m/s
 * (Perry & Burnfield; Winter). Son valores APROXIMADOS: sirven para animar de forma creíble, no para uso clínico.
 *  - cadera: flexión ~30° en el contacto, extensión máxima ~-10° al final del apoyo, flexión de nuevo en balanceo.
 *  - rodilla: ~18° en la respuesta a la carga, ~5° en apoyo medio, pico ~62–65° en balanceo.
 *  - tobillo (dorsiflexión +): plantarflexión ~-6° al apoyar, dorsiflexión ~10° al final del apoyo, empuje ~-20°.
 */
export const GAIT = {
  period: 1.2, // segundos por ciclo (doble paso) a 1.0 m/s
  hipArc: 40, // arco de cadera de las curvas de abajo, en grados
  hipMean: 10,
  hip: periodic([[0, 30], [0.1, 26], [0.3, 6], [0.48, -9], [0.56, -9], [0.66, 5], [0.78, 24], [0.9, 32]]),
  knee: periodic([[0, 6], [0.08, 18], [0.16, 19], [0.3, 8], [0.42, 5], [0.5, 14], [0.58, 34], [0.66, 52],
    [0.73, 64], [0.8, 52], [0.88, 26], [0.96, 8]]),
  ankleDF: periodic([[0, 0], [0.07, -6], [0.16, -1], [0.3, 6], [0.44, 10], [0.52, 3], [0.58, -12], [0.63, -20],
    [0.7, -8], [0.78, -2], [0.9, 1]]),
  legLength: 0.9, // m, cadera-tobillo aproximada del avatar (1.70 m de altura)
};

/** Canales y valores por defecto de una postura relajada (brazos colgando, todo neutro). */
export function neutral() {
  const c = {
    'pelvis.tilt': 0, 'pelvis.obl': 0, 'pelvis.rot': 0, 'pelvis.shift': 0, 'pelvis.lift': 0, 'pelvis.advance': 0,
    'spine.flex': 0, 'spine.bend': 0, 'spine.rot': 0,
    'neck.flex': 0, 'neck.bend': 0, 'neck.rot': 0,
    'head.flex': 0, 'head.bend': 0, 'head.rot': 0,
    'gaze.x': 0, 'gaze.y': 0,
    'face.blink': 0, 'face.smile': 0, 'face.brow': 0, 'face.jaw': 0, 'face.funnel': 0,
  };
  for (const s of ['L', 'R']) {
    c[`${s}.shoulder.abd`] = 8;
    c[`${s}.shoulder.flex`] = 3;
    c[`${s}.shoulder.elev`] = 0;
    c[`${s}.elbow.flex`] = 14;
    c[`${s}.hip.flex`] = 0;
    c[`${s}.hip.abd`] = 0;
    c[`${s}.knee.flex`] = 0;
    c[`${s}.ankle.pf`] = 0;
  }
  return c;
}
